import type { GameInviteView } from "../api/client";

/**
 * HOW LONG AN INVITE HAS LEFT, MEASURED ON *THIS DEVICE'S* CLOCK.
 *
 * THE BUG THIS EXISTS TO KILL (reported 2026-08-17, tg 1259386774): an invite's liveness was
 * decided by `Date.parse(invite.expiresAt) > Date.now()` — the SERVER's absolute instant against
 * the DEVICE's wall clock. Those are two different clocks. A phone running more than a TTL ahead
 * of real time therefore saw EVERY incoming invite as already expired, and every path that could
 * have offered پیوستن/رد refused: the push, the toast tap, and the boot sweep all shared that one
 * test. What still fired was the TOAST, which has no such gate — so the player was told «X invited
 * you» and given no way to answer, permanently. Measured on prod: 57 invites received, ZERO ever
 * accepted or declined, against a 51% platform act rate, with the push provably arriving (the
 * client refetched `/invites/incoming` 200 in the same second each invite was created).
 *
 * THE FIX IS TO COMPARE LIKE WITH LIKE. A DURATION is clock-independent; an INSTANT is not. So we
 * take how much time the invite has left — from the server (`remainingMs`), or failing that from
 * the difference of two SERVER timestamps (`expiresAt − createdAt`, which is the TTL and cancels
 * the clock out) — and anchor it against `Date.now()` AT THE MOMENT WE RECEIVE IT. From then on
 * both ends of every comparison are on the device's own clock, and a skewed clock only makes the
 * countdown run at the same wrong speed as everything else on that phone, which is to say: right.
 *
 * The server stays the authority on whether an accept actually succeeds — a genuinely dead invite
 * comes back 409 and the prompt renders that reason in Persian. That is why the fallbacks here all
 * fail OPEN (treat an un-anchored invite as fresh): showing a prompt that might be refused is a
 * recoverable annoyance; refusing to show one is the defect being fixed.
 */

/**
 * Report a device↔server clock gap at or beyond this. Comfortably under the 60s invite TTL that
 * the old comparison broke at, and far above ordinary NTP jitter, so a row on the ledger means a
 * phone genuinely out of step rather than noise.
 */
export const CLOCK_SKEW_REPORT_MS = 30_000;

/** An invite carrying the device-clock deadline we anchored when it arrived. */
export type ClientInvite = GameInviteView & { readonly deadlineMs?: number };

/**
 * The invite's TTL — how long it lives in total, or (when the server says so) how much of that is
 * LEFT. Both forms are durations or differences of server timestamps, so neither can be poisoned
 * by the device clock.
 */
export function inviteTtlMs(invite: GameInviteView): number {
  if (typeof invite.remainingMs === "number" && Number.isFinite(invite.remainingMs)) {
    return Math.max(0, invite.remainingMs);
  }
  const span = Date.parse(invite.expiresAt) - Date.parse(invite.createdAt);
  return Number.isFinite(span) ? Math.max(0, span) : 0;
}

/**
 * The device-clock instant this invite dies.
 *
 * An invite that never went through {@link anchorInvites} (a hand-built view, or the outgoing
 * side, which has no countdown) is read as freshly-received — fail open, see the header.
 */
export function inviteDeadlineMs(invite: ClientInvite, now: number = Date.now()): number {
  return invite.deadlineMs ?? now + inviteTtlMs(invite);
}

/** Is this invite still worth offering? PENDING and not yet past its anchored deadline. */
export function isLiveInvite(invite: ClientInvite, now: number = Date.now()): boolean {
  return invite.status === "PENDING" && inviteDeadlineMs(invite, now) > now;
}

/**
 * Anchor a freshly-fetched incoming list against the device clock.
 *
 * `known` carries the invites we had already anchored. When the server tells us what is LEFT
 * (`remainingMs`) we re-anchor on every fetch, because that number is authoritative and shrinking.
 * When it does not, we KEEP the earlier deadline: re-deriving it from the TTL would hand the same
 * invite a fresh full minute on every refresh, and this list is refetched on every push.
 */
export function anchorInvites(
  fetched: readonly GameInviteView[],
  known: readonly ClientInvite[],
  now: number = Date.now(),
): ClientInvite[] {
  return fetched.map((invite) => {
    if (typeof invite.remainingMs === "number" && Number.isFinite(invite.remainingMs)) {
      return { ...invite, deadlineMs: now + Math.max(0, invite.remainingMs) };
    }
    const prior = known.find((k) => k.id === invite.id)?.deadlineMs;
    return { ...invite, deadlineMs: prior ?? now + inviteTtlMs(invite) };
  });
}

/**
 * How far this device's clock sits from the server's, read off an invite the server created a
 * moment ago (the push path refetches within a second of creation, so `createdAt` is ≈ now).
 * Positive = the phone runs FAST, which is the direction that used to swallow invites.
 */
export function deviceClockSkewMs(invite: GameInviteView, now: number = Date.now()): number {
  const created = Date.parse(invite.createdAt);
  return Number.isFinite(created) ? now - created : 0;
}
